import { Trans } from 'react-i18next';

import {
    faArrowRightArrowLeft,
    faArrowRightFromBracket,
    faArrowRightToBracket,
    faCalendarCirclePlus,
    faClipboardList,
    faDiamondExclamation,
    faExclamationSquare,
} from '@fortawesome/pro-duotone-svg-icons';
import { t } from 'i18next';

function getOrderReference(notification = {}, fallback = '--') {
    return notification.templateParams?.order?.shipperOrderReference || fallback;
}

function getOrderType(notification = {}) {
    return notification.templateParams?.order?.orderType ?? null;
}

function byOrderType(orderType, { EXIT, ENTRY, fallback }) {
    if (orderType === 'EXIT') return EXIT;
    if (orderType === 'ENTRY') return ENTRY;
    return fallback;
}

function getOrderIcon(notification = {}) {
    const orderType = getOrderType(notification);
    return byOrderType(orderType, {
        EXIT: faArrowRightFromBracket,
        ENTRY: faArrowRightToBracket,
        fallback: faArrowRightArrowLeft,
    });
}

function getOrderUrl(notification = {}, tabId = null, extraSearchParams = null) {
    const orderId = notification.templateParams?.order?.id;
    if (!orderId) {
        return '/exit-orders/';
    }

    const orderType = notification.templateParams?.order?.orderType;
    const search = extraSearchParams ? `?${extraSearchParams}` : '';

    if (orderType === 'EXIT' || orderType === 'ENTRY') {
        const prefix = orderType === 'EXIT' ? 'exit-orders' : 'entry-orders';
        return `/${prefix}/${orderId}/${tabId ? `${tabId}/` : ''}${search}`;
    }

    // TRANSPORT and unknown order types are handled by routes that only understand the legacy `#hash` tab format.
    const baseUrl = orderType === 'TRANSPORT' ? `/transports/${orderId}/` : `/orders/${orderId}/`;
    return `${baseUrl}${search}${tabId ? `#${tabId}` : ''}`;
}

function getAdditionalRequestReference(notification = {}) {
    return notification.templateParams?.additionalRequest?.iid || '--';
}

function getAdditionalRequestUrl(notification = {}, suffix = '') {
    const additionalRequestId =
        notification.templateParams?.additionalRequest?.publicId || notification.templateParams?.additionalRequestId;

    if (!additionalRequestId) {
        return '/additional-requests/';
    }

    return `/additional-requests/${additionalRequestId}${suffix}`;
}

function getIncidentsUrl(notification = {}, suffix = '') {
    const incident = notification.templateParams?.incident;

    if (incident) {
        return `/incidents/?incidentId=${incident.id}${suffix}`;
    }

    return `/incidents/${suffix}`;
}

export const templates = {
    comment_added_to_order: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Comment');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New comment on exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New comment on entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New comment on order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            const commentId = notification.templateParams?.orderComment?.id;
            if (!commentId) {
                return getOrderUrl(notification, 'activities');
            }

            return getOrderUrl(notification, 'activities', `hlCommentId=comment-${commentId}`);
        },
    },
    mentioned_in_order_comment: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Comment');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New comment on exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New comment on entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New comment on order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification, 'activities');
        },
    },
    comment_added_to_order_incident: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Comment');
        },
        icon() {
            return faDiamondExclamation;
        },
        content(notification = {}) {
            const incidentReference = notification.templateParams?.incident?.iid || '--';
            return (
                <Trans
                    i18nKey='New comment on incident <0>{{ incidentReference }}</0>.'
                    values={{ incidentReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getIncidentsUrl(notification);
        },
    },
    mentioned_in_incident_comment: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Comment');
        },
        icon() {
            return faDiamondExclamation;
        },
        content(notification = {}) {
            const incidentReference = notification.templateParams?.incident?.iid || '--';
            return (
                <Trans
                    i18nKey='New comment on incident <0>{{ incidentReference }}</0>.'
                    values={{ incidentReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getIncidentsUrl(notification);
        },
    },
    order_incident_created: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Incident');
        },
        icon() {
            return faDiamondExclamation;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);

            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New incident on exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New incident on entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New incident on order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} incidents created'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getIncidentsUrl(notification);
        },
    },
    order_incident_closed: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Incident');
        },
        icon() {
            return faDiamondExclamation;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Incident closed on exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Incident closed on entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Incident closed on order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getIncidentsUrl(notification);
        },
    },
    order_incident_canceled: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Incident');
        },
        icon() {
            return faDiamondExclamation;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Incident canceled on exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Incident canceled on entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Incident canceled on order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getIncidentsUrl(notification);
        },
    },
    // @deprecated Replaced by exit_order_created / entry_order_created.
    // Safe to remove after 2026-06-12 (in_app_notifications are purged monthly).
    order_created_and_ready_to_be_executed_to_warehouse: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} orders created'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    // @deprecated Replaced by exit_order_created / entry_order_created.
    // Safe to remove after 2026-06-12 (in_app_notifications are purged monthly).
    order_created_by_warehouse_to_shipper: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} orders created'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    order_updated_by_shipper_to_warehouse: {
        user(notification = {}) {
            return notification.templateParams?.updatedBy;
        },
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> updated.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> updated.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> updated.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    order_updated_by_warehouse_to_shipper: {
        user(notification = {}) {
            return notification.templateParams?.updatedBy;
        },
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> updated.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> updated.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> updated.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    exit_order_blocked: {
        user() {
            return null;
        },
        title() {
            return t('Issue');
        },
        icon() {
            return faExclamationSquare;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            return (
                <Trans
                    i18nKey='New exit <0>{{ orderReference }}</0> blocked.'
                    values={{ orderReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} exits blocked'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    entry_order_blocked: {
        user() {
            return null;
        },
        title() {
            return t('Issue');
        },
        icon() {
            return faExclamationSquare;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            return (
                <Trans
                    i18nKey='Entry <0>{{ orderReference }}</0> blocked.'
                    values={{ orderReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} entries blocked'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    exit_order_created: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Order');
        },
        icon() {
            return faArrowRightFromBracket;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            return (
                <Trans
                    i18nKey='New exit <0>{{ orderReference }}</0>.'
                    values={{ orderReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} exits created'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    entry_order_created: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Order');
        },
        icon() {
            return faArrowRightToBracket;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            return (
                <Trans
                    i18nKey='New entry <0>{{ orderReference }}</0>.'
                    values={{ orderReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} entries created'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    integration_issue: {
        user(notification = {}) {
            return {
                id: 'system',
                isService: true,
                // L'émetteur porte le connecteur, que le contenu ne répète donc pas. C'est aussi le
                // seul libellé d'une ligne de groupe, dont le contenu se réduit à un compteur, et un
                // groupe peut couvrir plusieurs instances : le connecteur y est le bon niveau.
                // `shipper` est le repli des notifications déjà en base, sans nom de connecteur.
                company: notification?.templateParams?.connectorName ?? notification?.templateParams?.shipper,
            };
        },
        title() {
            return t('Issue');
        },
        icon() {
            return faExclamationSquare;
        },
        content(notification = {}, user = {}) {
            const params = notification.templateParams ?? {};
            // `connector` est le repli des notifications déjà en base : ce paramètre y portait le nom
            // de l'instance, avec le nom du connecteur pour second choix.
            const instanceName = params.instanceName ?? params.connector;
            // Même condition que l'entrée Connectivité de la barre latérale : sans elle, l'écran des
            // jobs n'est pas proposé, et nommer un job que le lecteur ne peut pas ouvrir l'égare.
            const seesConnectivity =
                !!user?.logisticProvider?.settings?.integrationMonitoringEnabled ||
                !!user?.customer?.settings?.integrationMonitoringEnabled;
            const lines = [
                instanceName && { label: t('Instance'), value: instanceName },
                seesConnectivity && params.job?.id && { label: t('Job'), value: params.job.id },
            ].filter(Boolean);
            return (
                <>
                    <span>{t('Integration error')}</span>
                    {lines.map((line) => (
                        <span
                            key={line.label}
                            className='block'
                        >
                            <span className='text-gray-600'>{line.label} : </span>
                            <span className='text-gray-800'>{line.value}</span>
                        </span>
                    ))}
                </>
            );
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} integration errors'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            const job = notification.templateParams?.job;
            if (job?.id) {
                return `/integrations/jobs/?selectedJob=${job?.id}`;
            }
            return '/integrations/jobs/';
        },
    },
    additional_request_created: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Additional request');
        },
        icon() {
            return faClipboardList;
        },
        content(notification = {}) {
            const additionalRequestReference = getAdditionalRequestReference(notification);
            return (
                <Trans
                    i18nKey='New additional request <0>{{ additionalRequestReference }}</0>.'
                    values={{ additionalRequestReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getAdditionalRequestUrl(notification);
        },
    },
    additional_request_edited: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Additional request');
        },
        icon() {
            return faClipboardList;
        },
        content(notification = {}) {
            const additionalRequestReference = getAdditionalRequestReference(notification);
            return (
                <Trans
                    i18nKey='Additional request <0>{{ additionalRequestReference }}</0> updated.'
                    values={{ additionalRequestReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getAdditionalRequestUrl(notification);
        },
    },
    additional_request_new_comment: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Additional request');
        },
        icon() {
            return faClipboardList;
        },
        content(notification = {}) {
            const additionalRequestReference = getAdditionalRequestReference(notification);
            return (
                <Trans
                    i18nKey='New comment on additional request <0>{{ additionalRequestReference }}</0>.'
                    values={{ additionalRequestReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getAdditionalRequestUrl(notification);
        },
    },
    mentioned_in_additional_request_comment: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Additional request');
        },
        icon() {
            return faClipboardList;
        },
        content(notification = {}) {
            const additionalRequestReference = getAdditionalRequestReference(notification);
            return (
                <Trans
                    i18nKey='New comment on additional request <0>{{ additionalRequestReference }}</0>.'
                    values={{ additionalRequestReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getAdditionalRequestUrl(notification);
        },
    },
    document_added_to_order: {
        user(notification = {}) {
            return notification.templateParams?.createdBy;
        },
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New document on exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New document on entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New document on order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification, 'documents');
        },
    },
    order_canceled_by_shipper_to_warehouse: {
        user(notification = {}) {
            return notification.templateParams?.canceledBy;
        },
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> canceled.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> canceled.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> canceled.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification, 'activities');
        },
    },
    order_canceled_by_warehouse_to_shipper: {
        user(notification = {}) {
            return notification.templateParams?.canceledBy;
        },
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> canceled.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> canceled.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> canceled.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    order_completed_with_adjustment_to_warehouse: {
        user(notification = {}) {
            return notification.templateParams?.completedBy;
        },
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> completed with quantity adjustments.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> completed with quantity adjustments.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> completed with quantity adjustments.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    order_completed_with_adjustment_to_shipper: {
        user(notification = {}) {
            return notification.templateParams?.completedBy;
        },
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> completed with quantity adjustments.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> completed with quantity adjustments.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> completed with quantity adjustments.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    timeslot_confirmed_by_shipper_to_warehouse: {
        user(notification = {}) {
            return notification.templateParams?.confirmedBy;
        },
        title() {
            return t('Scheduling');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Time slot confirmed for exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Time slot confirmed for entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Time slot confirmed for order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    timeslot_confirmed_by_warehouse_to_shipper: {
        user(notification = {}) {
            return notification.templateParams?.confirmedBy;
        },
        title() {
            return t('Scheduling');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Time slot confirmed for exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Time slot confirmed for entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Time slot confirmed for order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    order_completed: {
        user(notification = {}) {
            return notification.templateParams?.completedBy;
        },
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> completed.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> completed.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> completed.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    exit_order_marked_as_delivered: {
        user(notification = {}) {
            return notification.templateParams?.markedBy;
        },
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> delivered.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> delivered.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> delivered.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    unconfirmed_order_creation: {
        user(notification = {}) {
            return {
                id: 'system',
                isService: true,
                company: notification.templateParams?.customerCompany,
            };
        },
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification, '');
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New unconfirmed exit <0>{{ orderReference }}</0> to confirm.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New unconfirmed entry <0>{{ orderReference }}</0> to confirm.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New unconfirmed order <0>{{ orderReference }}</0> to confirm.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            if (notification.templateParams?.unconfirmedOrderId) {
                return `/unconfirmed-orders/${notification.templateParams?.unconfirmedOrderId}`;
            }
            return '/unconfirmed-orders';
        },
    },
    failure_auto_pdf_order_creation: {
        user() {
            return null;
        },
        title() {
            return t('Issue');
        },
        icon() {
            return faExclamationSquare;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification, '');
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Failed to automatically generate the exit <0>{{ orderReference }}</0> from the PDF.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Failed to automatically generate the entry <0>{{ orderReference }}</0> from the PDF.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Failed to automatically generate the order <0>{{ orderReference }}</0> from the PDF.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            if (notification.templateParams?.unconfirmedOrderId) {
                return `/unconfirmed-orders/${notification.templateParams?.unconfirmedOrderId}`;
            }
            return '/unconfirmed-orders';
        },
    },
    need_to_confirm_planned_execution_datetime_to_shipper: {
        user(notification = {}) {
            return notification.templateParams?.suggestedBy;
        },
        title() {
            return t('Scheduling');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New time slot requested for exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New time slot requested for entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New time slot requested for order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    need_to_confirm_planned_execution_datetime_to_warehouse: {
        user(notification = {}) {
            return notification.templateParams?.suggestedBy;
        },
        title() {
            return t('Scheduling');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New time slot requested for exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New time slot requested for entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New time slot requested for order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    master_item_blocked_on_integration_sync_conditions: {
        user() {
            return null;
        },
        title() {
            return t('Issue');
        },
        icon() {
            return faExclamationSquare;
        },
        content() {
            return t('New reference blocked');
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} references blocked'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            const masterItem = notification.templateParams?.masterItem;
            if (masterItem?.id) {
                return `/inventory/items/${masterItem.id}`;
            }
            return '/inventory/items/';
        },
    },
    appointment_rescheduled_by_carrier: {
        user(notification = {}) {
            return {
                id: 'system',
                isService: true,
                company: notification.templateParams?.appointment?.carrierCompany,
            };
        },
        title() {
            return t('Scheduling');
        },
        icon() {
            return faCalendarCirclePlus;
        },
        content() {
            return t('Appointment rescheduled.');
        },
        url(notification = {}) {
            const appointmentId = notification.templateParams?.appointment?.id;
            if (appointmentId) {
                return `/appointments/list/${appointmentId}`;
            }
            return '/appointments/list/';
        },
    },
    appointment_confirmation_for_lsp: {
        user(notification = {}) {
            return {
                id: 'system',
                isService: true,
                company: notification.templateParams?.appointment?.carrierCompany,
            };
        },
        title() {
            return t('Scheduling');
        },
        icon() {
            return faCalendarCirclePlus;
        },
        content() {
            return t('Appointment confirmed.');
        },
        url(notification = {}) {
            const appointmentId = notification.templateParams?.appointment?.id;
            if (appointmentId) {
                return `/appointments/list/view/appointment/${appointmentId}`;
            }
            return '/appointments/list/';
        },
    },
};
